import React, { useMemo } from 'react';
import { useLocalStorage } from 'apps/sigma2-ui/src/lib/hooks/useLocalStorage';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { SideNavigationMenu } from 'apps/sigma2-ui/src/components/SideNavigationMenu';
import { TreatmentCart } from 'apps/sigma2-ui/src/components/TreatmentCart';
import { Scrollable } from 'apps/sigma2-ui/src/components/Scrollable';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { z } from 'zod';
import { SideNavigationMenuCollapsedCacheKey } from 'apps/sigma2-ui/src/constants/usable-cache-keys';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { WarningBanner } from 'apps/sigma2-ui/src/components/WarningBanner';
import { WARNING_BANNER_HEIGHT_CLASS } from 'apps/sigma2-ui/src/components/WarningBanner';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AssistantModal } from 'apps/sigma2-ui/src/components/assistant-ui/assistant-modal';
import { isShowChatbotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isEntitledToAi } from 'libs/end-to-end/src/group-entitlements';

export const MainLayout = ({
  children,
  scrollableRef,
}: {
  children: React.ReactNode;
  showBackToTop: boolean;
  scrollableRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { myUser, startLoggingOut, featureFlags } = useUser();
  const { myObservation, staleResultShowing } = useObservation();
  const language = useLanguage();

  const [isCollapsed, setIsCollapsed] = useLocalStorage({
    defaultValue: false,
    key: SideNavigationMenuCollapsedCacheKey,
    schema: z.boolean(),
  });

  const [isTreatmentCartActive, toggleIsTreatmentCartActive] = useToggle({
    initialState: false,
  });

  const organizationName = myUser?.organization?.name ?? 'Unknown organization';

  const leadershipFlag = Boolean(featureFlags?.leadershipFlag);

  const nlpGraphAvailable = Boolean(featureFlags?.dangerouslyExposeNlpGraph);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const showChatbot = useMemo(
    () =>
      isShowChatbotEnabledForRevision({
        revisionFlags: myObservation,
      }) &&
      isEntitledToAi({
        features: myObservation?.features ?? [],
      }),
    [myObservation],
  );

  return (
    <div className={'relative w-screen h-screen overflow-hidden'}>
      <WarningBanner
        icon={IconInformationSvg}
        translatedContent={language.warningBannerDeploymentStaleResults()}
        isShowing={staleResultShowing}
      />

      <div className="flex flex-row">
        <SideNavigationMenu
          organizationName={organizationName}
          isCollapsed={isMobile || isCollapsed}
          nlpGraphAvailable={nlpGraphAvailable}
          logoutTapped={() => {
            startLoggingOut(uiEnvironment.callbackLogoutUri);
          }}
          toggleIsCollapsed={() => {
            setIsCollapsed((prev) => !prev);
          }}
          toggleIsTreatmentCartActive={toggleIsTreatmentCartActive}
          leadershipFlag={leadershipFlag}
          myObservation={myObservation}
          warningIsShowing={staleResultShowing}
        />

        <div className={'relative flex-1 h-screen'}>
          <Scrollable
            className="w-full h-full absolute left-0 top-0"
            sneakyPeakyHeight={0}
            trackWidthClass="w-4 z-scrollbar-track"
            trackHandleWidthClass="w-[10px]"
            dataTestId={'mainScrollable'}
            ref={scrollableRef}
          >
            <div id="top"></div>
            {staleResultShowing && (
              <div className={WARNING_BANNER_HEIGHT_CLASS} />
            )}
            {children}
          </Scrollable>
        </div>

        <TreatmentCart
          show={isTreatmentCartActive}
          onClose={toggleIsTreatmentCartActive}
        />
      </div>
      {showChatbot && <AssistantModal />}
    </div>
  );
};
